import { FC, useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { trpc } from 'src/trpc';
import { Loader } from 'components/Loader';
import { safeMoneyFromCents, safeMoneyFromDollars } from '@libs/services/money';
import { CreditCard, DollarSign, Link } from 'lucide-react';
import { useToast } from 'components/useToast';
import { createPaymentSchema, CreatePaymentForm } from '@libs/schemas';
import { StripeForm } from 'components/StripeForm';
import { platformStripePromise, stripePromise } from 'src/utils/stripe';
import { NumericFormat } from 'react-number-format';

interface PaymentIntentData {
  id: string;
  clientSecret: string | null;
  connectAccountId: string;
  amount: number;
}

interface CreatePaymentDrawerProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  userId: string; // User ID to fetch payment methods from Stripe
}

export const CreatePaymentDrawer: FC<CreatePaymentDrawerProps> = ({
  open,
  onClose,
  orderId,
  userId,
}) => {
  const [amountInput, setAmountInput] = useState<number | undefined>(undefined);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntentData | null>(null);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');
  const { successToast, errorToast } = useToast();

  // Fetch payment methods directly from Stripe
  const { data: paymentMethodsData, isLoading: isLoadingPaymentMethods } =
    trpc.admin.order.adminGetCustomerPaymentMethods.useQuery(
      { userId },
      {
        enabled: !!userId,
        staleTime: 30000, // Cache for 30 seconds
      },
    );

  const paymentMethods = paymentMethodsData?.paymentMethods || [];
  const hasExistingCardPayment = paymentMethods.length > 0;

  // Set default selected payment method when payment methods are loaded
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPaymentMethodId) {
      setSelectedPaymentMethodId(paymentMethods[0].id);
    }
  }, [paymentMethods, selectedPaymentMethodId]);

  const { register, handleSubmit, setValue, watch, reset } = useForm<CreatePaymentForm>({
    resolver: zodResolver(createPaymentSchema),
    defaultValues: {
      paymentType: hasExistingCardPayment ? 'existing-card' : 'card',
      amountInCents: 0,
    },
  });

  const paymentType = watch('paymentType');

  const utils = trpc.useUtils();

  const { mutate: createNonCardPayment, isPending: nonCardPending } =
    trpc.admin.order.adminCreateExternalPayment.useMutation({
      onSuccess: (data) => {
        successToast({
          title: 'Payment Created',
          description: data.message,
        });
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
        handleClose();
      },
      onError: () => {
        errorToast({
          title: 'Error Creating Payment',
          description: 'Failed to create payment. Please try again.',
        });
      },
    });

  const { mutate: chargeExistingCard, isPending: existingCardPending } =
    trpc.admin.order.adminChargeExistingPaymentMethod.useMutation({
      onSuccess: (data) => {
        successToast({
          title: 'Payment Processed',
          description: data.message,
        });
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
        handleClose();
      },
      onError: () => {
        errorToast({
          title: 'Error Processing Payment',
          description: 'Failed to charge existing payment method. Please try again.',
        });
      },
    });

  // Create payment intent for card payments
  const { mutate: createPaymentIntent, isPending: intentPending } =
    trpc.admin.order.adminCreateOrderPaymentIntent.useMutation({
      onSuccess: (data) => {
        setPaymentIntent(data);
        setShowStripeForm(true);
      },
      onError: (error) => {
        errorToast({
          title: 'Error Creating Payment Intent',
          description: error.message || 'Failed to create payment intent. Please try again.',
        });
      },
    });

  // Load Stripe instance when payment intent is created
  useEffect(() => {
    const loadStripeInstance = async () => {
      if (paymentIntent) {
        if (paymentIntent.connectAccountId) {
          const connectedInstance = await stripePromise(paymentIntent.connectAccountId);
          setStripeInstance(connectedInstance);
        } else {
          const platformInstance = await platformStripePromise();
          setStripeInstance(platformInstance);
        }
      }
    };

    loadStripeInstance();
  }, [paymentIntent]);

  const handleClose = () => {
    reset();
    setAmountInput(undefined);
    setPaymentIntent(null);
    setStripeInstance(null);
    setShowStripeForm(false);
    onClose();
  };

  const onSubmit = (data: CreatePaymentForm) => {
    if (data.paymentType === 'non-card') {
      if (!data.description || data.description.trim().length === 0) {
        errorToast({
          title: 'Description Required',
          description: 'Please provide a description for external payments.',
        });
        return;
      }

      createNonCardPayment({
        orderId,
        amountInCents: data.amountInCents,
        description: data.description.trim(),
      });
    } else if (data.paymentType === 'existing-card') {
      // Validate that a payment method is selected
      if (!selectedPaymentMethodId) {
        errorToast({
          title: 'Payment Method Required',
          description: 'Please select a payment method to charge.',
        });
        return;
      }

      // Charge existing card
      chargeExistingCard({
        orderId,
        amountInCents: data.amountInCents,
        paymentMethodId: selectedPaymentMethodId,
      });
    } else {
      // Create payment intent for new card payment
      createPaymentIntent({
        orderId,
        amountInCents: data.amountInCents,
      });
    }
  };

  const isPending = nonCardPending || intentPending || existingCardPending;

  // Stripe Elements options
  const stripeOptions: StripeElementsOptions | undefined = paymentIntent?.clientSecret
    ? {
        clientSecret: paymentIntent.clientSecret,
      }
    : undefined;

  if (isLoadingPaymentMethods) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
          <div className="grid h-full grid-rows-[auto_1fr_auto]">
            <SheetHeader className="border-b bg-white px-6 py-4">
              <SheetTitle className="text-left text-xl font-semibold text-gray-900">
                Record Payment
              </SheetTitle>
            </SheetHeader>
            <div className="overflow-y-auto px-6 py-6">
              <Loader title="Loading payment methods..." />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-xl font-semibold text-gray-900">
              Record Payment
            </SheetTitle>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className="overflow-y-auto px-6 py-6">
            {showStripeForm &&
            paymentIntent &&
            paymentIntent.clientSecret &&
            stripeInstance &&
            stripeOptions ? (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-lg font-medium">
                    Payment Amount: {safeMoneyFromCents(paymentIntent.amount).toFormat()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Complete the payment using your card details below
                  </p>
                </div>

                <Elements stripe={stripeInstance} options={stripeOptions}>
                  <StripeForm
                    onSuccess={() => {
                      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
                      handleClose();
                    }}
                  />
                </Elements>

                <Button
                  variant="outline"
                  onClick={() => setShowStripeForm(false)}
                  className="w-full"
                >
                  Back to Payment Options
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" id="payment-form">
                {/* Payment Type Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Payment Type</Label>
                  <RadioGroup
                    value={paymentType}
                    onValueChange={(value) =>
                      setValue('paymentType', value as 'card' | 'non-card' | 'existing-card')
                    }
                    className={`grid gap-4 ${hasExistingCardPayment ? 'grid-cols-3' : 'grid-cols-2'}`}
                  >
                    {hasExistingCardPayment && (
                      <Label
                        htmlFor="existing-card"
                        className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <RadioGroupItem value="existing-card" id="existing-card" />
                        <CreditCard className="h-4 w-4 text-green-600" />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            Existing Payment Method{paymentMethods.length > 1 ? 's' : ''}
                          </span>
                          <span className="text-xs text-gray-500">
                            {paymentMethods.length} payment method
                            {paymentMethods.length > 1 ? 's' : ''} on file
                          </span>
                        </div>
                      </Label>
                    )}
                    <Label
                      htmlFor="card"
                      className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <RadioGroupItem value="card" id="card" />
                      <CreditCard className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium">New Card</span>
                    </Label>
                    <Label
                      htmlFor="non-card"
                      className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <RadioGroupItem value="non-card" id="non-card" />
                      <DollarSign className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium">External Payment</span>
                    </Label>
                  </RadioGroup>
                </div>

                <Separator />

                {/* Payment Method Selection for Existing Cards and Link */}
                {paymentType === 'existing-card' && hasExistingCardPayment && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">
                      Select Payment Method
                    </Label>
                    <RadioGroup
                      value={selectedPaymentMethodId}
                      onValueChange={setSelectedPaymentMethodId}
                      className="space-y-2"
                    >
                      {paymentMethods.map((pm) => (
                        <Label
                          key={pm.id}
                          htmlFor={pm.id}
                          className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <RadioGroupItem value={pm.id} id={pm.id} />
                          {pm.type === 'card' ? (
                            <>
                              <CreditCard className="h-4 w-4 text-gray-600" />
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">
                                  {pm.card.charAt(0).toUpperCase() + pm.card.slice(1)} ••••{' '}
                                  {pm.lastFour}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Expires {pm.expMonth?.toString().padStart(2, '0')}/{pm.expYear}
                                </span>
                              </div>
                            </>
                          ) : pm.type === 'link' ? (
                            <>
                              <Link className="h-4 w-4 text-gray-600" />
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">Link Payment</span>
                                <span className="text-xs text-gray-500">{pm.email}</span>
                              </div>
                            </>
                          ) : null}
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Amount Input */}
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-sm font-medium text-gray-700">
                    Payment Amount
                  </Label>
                  <div className="relative">
                    <NumericFormat
                      customInput={Input}
                      prefix={'$'}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      type="text"
                      step="0.01"
                      placeholder="$150.00"
                      value={amountInput}
                      onValueChange={(values) => {
                        setAmountInput(values.floatValue || undefined);
                        setValue(
                          'amountInCents',
                          safeMoneyFromDollars(values.floatValue || undefined).getAmount(),
                        );
                      }}
                      className="w-full"
                      thousandSeparator={true}
                    />
                  </div>
                </div>

                {/* Description for External Payments */}
                {paymentType === 'non-card' && (
                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                      Payment Description
                    </Label>
                    <Input
                      id="description"
                      placeholder="e.g., Cash payment, Check #1234, Bank transfer"
                      {...register('description')}
                    />
                    <p className="text-xs text-gray-500">
                      Describe the payment method or reference
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Footer - Always Visible */}
          {!showStripeForm && (
            <SheetFooter className="border-t bg-gray-50 px-6 py-4">
              <div className="flex w-full justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isPending}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="payment-form"
                  disabled={isPending || !amountInput || amountInput <= 0}
                  className="w-full sm:w-auto text-white bg-orange-600 hover:bg-orange-700"
                >
                  {isPending ? (
                    <>
                      <Loader h={4} w={4} color="white" />
                      <span className="ml-2">Creating...</span>
                    </>
                  ) : (
                    'Create Payment'
                  )}
                </Button>
              </div>
            </SheetFooter>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
